import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { reviewsApi } from "@/api";
import type { ReviewFilter, UpsertReviewData } from "@/api";
import { QUERY_KEYS } from "@/constants";

// ─── Moderatsiya (xodimlar) ─────────────────────────────────────────────────

export const useReviewModerationStats = () =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_REVIEW_MODERATION_STATS],
    queryFn: () => reviewsApi.moderationStats(),
    select: res => res.data,
  });

export const useReviewModerationList = (filter: ReviewFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_REVIEW_MODERATION_LIST, filter],
    queryFn: () => reviewsApi.moderationList(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

const useInvalidateReviewModeration = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_REVIEW_MODERATION_LIST] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_REVIEW_MODERATION_STATS] });
  };
};

export const useApproveReview = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateReviewModeration();
  return useMutation({
    mutationFn: (id: number) => reviewsApi.approve(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("REVIEW_MODERATION.APPROVED_TOAST"));
    },
  });
};

export const useRejectReview = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateReviewModeration();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => reviewsApi.reject(id, reason),
    onSuccess: () => {
      invalidate();
      toast.success(t("REVIEW_MODERATION.REJECTED_TOAST"));
    },
  });
};

/** Kurs sharhlarining ommaviy ro'yxati. */
export const useCourseReviews = (courseId: number | undefined, filter: ReviewFilter) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_REVIEWS, courseId, filter],
    queryFn: () => reviewsApi.getForCourse(courseId as number, filter),
    enabled: !!courseId,
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });
};

const useInvalidateReviews = (courseId: number) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_REVIEWS, courseId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, courseId] });
  };
};

/** Yangi sharh yozadi (o'quvchi bir nechtasini yoza oladi). */
export const useCreateReview = (courseId: number) => {
  const invalidate = useInvalidateReviews(courseId);
  // Bu yerda toast yo'q — muvaffaqiyatda forma "rahmat" oynasini ko'rsatadi.
  return useMutation({
    mutationFn: (data: UpsertReviewData) => reviewsApi.create(courseId, data),
    onSuccess: invalidate,
  });
};

/** O'z sharhlarimdan birini tahrirlaydi (u yana ko'rib chiqishga tushadi). */
export const useUpdateReview = (courseId: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateReviews(courseId);
  return useMutation({
    mutationFn: (vars: { id: number; data: UpsertReviewData }) =>
      reviewsApi.update(courseId, vars.id, vars.data),
    onSuccess: () => {
      invalidate();
      toast.success(t("REVIEWS.UPDATED_TOAST"));
    },
  });
};

/** O'z sharhlarimdan birini o'chiradi. */
export const useDeleteReview = (courseId: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateReviews(courseId);
  return useMutation({
    mutationFn: (id: number) => reviewsApi.remove(courseId, id),
    onSuccess: () => {
      invalidate();
      toast.success(t("REVIEWS.DELETED_TOAST"));
    },
  });
};
